import { AstroPayCardPayment, AstroPayCore, AstroPayFullCheckout } from '@astropay/payments-lib';
import { APAvailableLanguages, APEnvironment } from '@astropay/payments-lib/types';
import { useMessage } from '@core/context/messageContext';
import { TAstroPayHttpError } from '@core/httpClient/types';
import { AstroPayI18n } from '@core/i18n';
import { ROUTES } from '@routes/paths';
import AmplitudeAnalytics from '@services/amplitude';
import { PaymentService } from '@services/payment';
import { ERequestedPaymentMethod, TPaymentDetails } from '@services/payment/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { APButton } from '@components/atoms/APButton';
import { APIcon } from '@components/atoms/APIcon';
import { APLink } from '@components/atoms/APLink';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@components/atoms/Collapsible';
import { Separator } from '@components/atoms/Separator';
import { Typography } from '@components/atoms/Typography';
import { ProductsList } from '@components/organisms/ProductsList';
import { RowsSkeleton } from '@components/organisms/skeleton/Rows';
import { TableSkeleton } from '@components/organisms/skeleton/Table';
import { SubtotalList } from '@components/organisms/SubtotalList';
import { showAmount } from '@utils/currency';
import { buildPathWithParams, parseQueryData, validateUrlFormat } from '@utils/urls';

import { TEncodeData } from './type';

const DEFAULT_COUNTRY = 'AR';

export const PaymentPage = () => {
  const { paymentExternalId } = useParams();
  const navigate = useNavigate();
  const { setMessage } = useMessage();

  const [paymentDetails, setPaymentDetails] = useState<TPaymentDetails>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const paymentComponentRef = useRef<AstroPayFullCheckout | AstroPayCardPayment | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const encodedData = urlParams.get('data');

  const formatEnv = (env: APEnvironment | string) => {
    switch (env) {
      case 'tst':
      case 'test':
        return 'test' as APEnvironment;
      case 'stg':
      case 'staging':
      case 'sandbox':
        return 'sandbox';
      case 'prod':
      case 'production':
      default:
        return 'production';
    }
  };

  const hasSummaryAvailable = useMemo(() => {
    if (
      paymentDetails?.order.items ||
      paymentDetails?.order.summary?.fees?.value ||
      paymentDetails?.order.summary?.shipping?.value
    )
      return true;
    return false;
  }, [paymentDetails]);

  let appIdToken = 'unknown';
  let environment: APEnvironment = 'production';
  if (encodedData) {
    try {
      const parsedData = parseQueryData<TEncodeData>(encodedData);

      if (parsedData?.app_id) appIdToken = parsedData.app_id;
      if (parsedData?.environment) environment = formatEnv(parsedData.environment);
    } catch (err) {
      console.warn('Invalid base64 "data" param:', err);
    }
  }

  useEffect(() => {
    const initAstroPayLib = () => {
      AstroPayCore.init(appIdToken, {
        environment,
        styles: { borderRadiusWrapper: 20, width: '100%', paddingWrapper: 0 },
        language: AstroPayI18n.getCurrent() as APAvailableLanguages,
        theme: 'light',
        tracking: false,
      });
    };

    const fetchPaymentDetails = async () => {
      try {
        // Handle Base64 query param "data");
        const response = await PaymentService.getPaymentDetails(paymentExternalId || '');
        setPaymentDetails(response);

        // ------------amp event -------------
        AmplitudeAnalytics.hostedCheckoutImpression(
          response.paymentExternalId,
          response.paymentInfo.merchant.name,
        );
        // ------------amp event -------------

        if (paymentContainerRef.current) {
          paymentContainerRef.current.innerHTML = '';

          let paymentComponent;
          if (response.requestedPaymentMethod === ERequestedPaymentMethod.CARD) {
            paymentComponent = new AstroPayCardPayment({
              amount: response.order.paymentAmount.amount,
              currency: response.order.paymentAmount.currency,
              country: DEFAULT_COUNTRY,
              paymentExternalId: response.paymentExternalId,
              showPaymentSummary: false,
              onSuccess: () =>
                handleSuccessCallback(
                  response?.paymentInfo.merchant.successUrl ?? response?.paymentInfo.merchant.url,
                ),
            });
          } else {
            paymentComponent = new AstroPayFullCheckout({
              amount: response.order.paymentAmount.amount,
              currency: response.order.paymentAmount.currency,
              country: DEFAULT_COUNTRY,
              showTitle: false,
              paymentExternalId: response.paymentExternalId,
              showDescription: false,
              onSuccess: () =>
                handleSuccessCallback(
                  response?.paymentInfo.merchant.successUrl ?? response?.paymentInfo.merchant.url,
                ),
            });
          }

          paymentComponent.render(paymentContainerRef.current);
          paymentComponentRef.current = paymentComponent;
        }
      } catch (error) {
        const err = error as TAstroPayHttpError;
        setMessage(err);

        navigate(
          buildPathWithParams(ROUTES.PAYMENT_STATUS, {
            paymentExternalId: paymentExternalId || '',
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    initAstroPayLib();
    fetchPaymentDetails();
  }, []);

  const handleSuccessCallback = (successUrl?: string) => {
    if (successUrl) window.open(validateUrlFormat(successUrl), '_self');
  };

  const onReturnToMerchant = () => {
    const cancelUrl =
      paymentDetails?.paymentInfo.merchant.cancelUrl || paymentDetails?.paymentInfo.merchant.url;

    if (paymentDetails && cancelUrl) {
      // ------------amp event -------------
      AmplitudeAnalytics.hostedCheckoutPaymentCancelClicked(
        paymentDetails.paymentExternalId,
        paymentDetails.paymentInfo.merchant.name,
      );
      // ------------amp event -------------

      window.open(validateUrlFormat(cancelUrl), '_self');
    }
  };

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto bg-white rounded-2xl p-6 border mb-10">
      {/* Return Action */}
      <div className="mb-2">
        <div
          className="inline-flex items-center cursor-pointer w-auto"
          onClick={onReturnToMerchant}
        >
          <APIcon img="arrowLeftLine" size={20} />
          <Typography
            textKey="Checkout.return"
            variant="span"
            className="text-[16px] text-[#64748B]"
          />
        </div>
      </div>
      {/* Title section */}
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex-1">
          <div className="flex items-center">
            <Typography textKey="Checkout.summary" weight={500} className=" text-[24px]" />
          </div>
          <Typography textKey="Checkout.reviewYourOrder" className="text-[16px] text-[#64748B]" />
        </div>
        <div className="">
          {isLoading ? (
            <RowsSkeleton />
          ) : (
            paymentDetails?.paymentInfo && (
              <div className="flex gap-1 px-4 py-2 my-4 rounded-lg bg-[#F9FAFB] justify-center border">
                <Typography textKey="Checkout.payingTo" />
                {paymentDetails?.paymentInfo.merchant.url ? (
                  <APLink
                    text={paymentDetails?.paymentInfo.merchant.name}
                    redirectUrl={paymentDetails?.paymentInfo.merchant.url}
                    className="truncate max-w-[200px]"
                  />
                ) : (
                  <Typography
                    text={paymentDetails?.paymentInfo.merchant.name}
                    weight={500}
                    className="truncate max-w-[200px]"
                  />
                )}
              </div>
            )
          )}
        </div>
      </div>

      {hasSummaryAvailable && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleContent className="transition-all duration-300 ease-in-out overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            {isLoading ? (
              <TableSkeleton rows={2} rowHeight={60} />
            ) : (
              paymentDetails &&
              paymentDetails.order.items &&
              paymentDetails.order.items.length > 0 && (
                <ProductsList
                  items={paymentDetails.order.items}
                  orderCurrency={paymentDetails.order.paymentAmount.currency}
                />
              )
            )}

            {isLoading ? (
              <>
                <Separator />
                <div className="space-y-3">
                  <TableSkeleton rows={2} showTitle={false} />
                </div>
                <Separator />
              </>
            ) : (
              paymentDetails?.order.summary && (
                <SubtotalList
                  summary={paymentDetails?.order.summary}
                  orderCurrency={paymentDetails.order.paymentAmount.currency}
                />
              )
            )}
          </CollapsibleContent>

          <CollapsibleTrigger className="w-full mb-6 pt-2 flex items-center justify-start gap-2 text-sm text-primary hover:text-primary/80 transition-colors">
            <Typography
              textKey={isOpen ? 'Checkout.hideOrderDetails' : 'Checkout.showOrderDetails'}
              variant="a"
            />
            <APIcon img={isOpen ? 'chevronUpFill' : 'chevronDownFill'} size={15} />
          </CollapsibleTrigger>
        </Collapsible>
      )}

      {/* Total */}
      {isLoading ? (
        <RowsSkeleton rowHeight={60} />
      ) : (
        <div className="flex justify-between items-center mb-2">
          <Typography textKey="Checkout.total" className="text-[16px]" weight={500} />

          <span className="text-2xl font-bold text-foreground">
            <Typography
              text={showAmount({
                value: paymentDetails?.order.paymentAmount.amount ?? 0,
                currency: paymentDetails?.order.paymentAmount.currency ?? '',
              })}
              className="text-[28px]"
              weight={500}
            />
          </span>
        </div>
      )}

      <div className="flex flex-col items-center justify-center gap-2 mt-6 ">
        <div
          ref={paymentContainerRef}
          id="payment-container"
          className="w-full"
          onClick={() => setIsOpen(false)}
        />

        {(paymentDetails?.paymentInfo.merchant.cancelUrl ||
          paymentDetails?.paymentInfo.merchant.url) && (
          <APButton
            variant="tertiary"
            size="large"
            textKey="CommonMessages.payment.cancel"
            className="pr-5"
            onClick={onReturnToMerchant}
          />
        )}
      </div>
    </div>
  );
};
